/* @flow */

//----------------------------------------------------------------------//
//                         Imported NPM modules                         //
//----------------------------------------------------------------------//

import React from 'react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import moment from 'moment';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import getMuiTheme from 'material-ui/styles/getMuiTheme';
import { DatePicker } from 'material-ui';

//----------------------------------------------------------------------//
//                     Imported app modules/assets                      //
//----------------------------------------------------------------------//

import styles from './ElectroDatePicker.module.scss';

//----------------------------------------------------------------------//
//                       Exported React component                       //
//----------------------------------------------------------------------//

type TypeProps = {
  field?: Object,
  startAdornment?: boolean,
  clearDate?: Function,
};

const calendarTheme = getMuiTheme({
  palette: {
    primary1Color: '#2a3338',
  },
  datePicker: {
    color: '#f26045',
    textColor: '#ffffff',
    calendarTextColor: '#2a3338',
    selectColor: '#f26045',
    selectTextColor: '#ffffff',
    calendarYearBackgroundColor: '#ffffff',
    headerColor: '#f26045',
  },
});

@observer
export default class ElectroDatePicker extends React.Component<*> {
  @observable value: Object = null;

  render(): React.Element<*> {
    /*eslint-disable max-len*/

    const { field, startAdornment, clearDate, ...other }: TypeProps = this.props;
    const isErrorClass: string = field?.error ? styles['is-error'] : '';
    const isDisabledClass: string = this.props.disabled ? styles['is-disabled'] : '';

    const errorMessage: Function = (formField: Object): React.Element => (
      <span className={styles['error-message']}>{formField.error}</span>
    );

    const clearDatePicker: Function = () => {
      if (this.props.clearDate) {
        this.props.clearDate();
      } else if (field) {
        field.clear();
      } else {
        this.value = null;
      }
    };

    let props: any;

    if (field) {
      props = {
        ...field.bind(),
        floatingLabelText: `${field.label} ${
          field.rules && field.rules.includes('required') ? '*' : ''
        }`,
        value: field.value ? field.value : null,
        onChange: (e: SyntheticInputEvent<*>, date: Date) => {
          field.value = date;
          field.validate();
        },
        errorText: field?.error ? errorMessage(field) : null,
        ...other,
      };
    } else {
      props = {
        value: this.value ? this.value : null,
        onChange: (e: SyntheticInputEvent<*>, date: Date) => {
          this.value = date;
        },
        ...other,
      };
    }

    return (
      <div className={`${styles['date-field']} ${isErrorClass} ${isDisabledClass}`}>
        {startAdornment && (
          <svg className={`${styles['icon-calendar']} ${styles['start-adornment']}`}>
            <use xlinkHref="#icon-calendar" />
          </svg>
        )}

        <MuiThemeProvider muiTheme={calendarTheme}>
          <DatePicker
            className={styles['date-picker']}
            formatDate={(date: Date) => moment(date).format('MMMM DD, YYYY')}
            {...props}
          />
        </MuiThemeProvider>

        {(field && field.value) || this.value || this.props.value ? (
          <svg className={styles['icon-close']} onClick={clearDatePicker}>
            <use xlinkHref="#icon-close" />
          </svg>
        ) : startAdornment === true ? (
          <svg className={styles['icon-dropdown']}>
            <use xlinkHref="#icon-drop-arrow-down" />
          </svg>
        ) : (
          <svg className={styles['icon-calendar']}>
            <use xlinkHref="#icon-calendar" />
          </svg>
        )}
      </div>
    );
  }
}
